import { fetchJSON as fetchAPI } from './client';

export type SREDashboard = {
  tenant_id: string;
  availability_pct: number;
  active_incidents: number;
  open_deployments: number;
  backups_24h: number;
  recoveries_validated: number;
  slos_met: number;
  slos_total: number;
  error_budget_remaining_pct: number;
  components_healthy: number;
  components_total: number;
  unread_notifications: number;
  recent_activity?: ActivityEntry[];
  generated_at: string;
};

export type DeploymentRecord = {
  id: string;
  service: string;
  version: string;
  strategy: string;
  status: string;
  region?: string;
  canary_pct?: number;
  smoke_passed: boolean;
  rolled_back: boolean;
  started_at: string;
  completed_at?: string;
};

export type BackupRecord = {
  id: string;
  type: string;
  status: string;
  size_bytes: number;
  version: number;
  region?: string;
  verified: boolean;
  started_at: string;
  completed_at?: string;
};

export type RecoveryRecord = {
  id: string;
  type: string;
  status: string;
  source_region?: string;
  target_region?: string;
  rpo_seconds?: number;
  rto_seconds?: number;
  validated: boolean;
  runbook_id?: string;
  started_at: string;
  completed_at?: string;
};

export type IncidentRecord = {
  id: string;
  title: string;
  description?: string;
  severity: string;
  status: string;
  service?: string;
  runbook_id?: string;
  created_at: string;
  updated_at: string;
};

export type Runbook = {
  id: string;
  title: string;
  category?: string;
  steps?: string[];
  automated: boolean;
};

export type SLODefinition = {
  id: string;
  service: string;
  name: string;
  target: number;
  error_budget: number;
  budget_used: number;
  budget_remaining: number;
  window_days: number;
};

export type SLIDefinition = {
  id: string;
  service: string;
  type: string;
  name: string;
  unit?: string;
  current: number;
  target: number;
};

export type ComponentHealth = {
  component: string;
  state: string;
  latency_ms?: number;
  error_rate?: number;
  replicas?: number;
  last_checked: string;
};

export type CapacityForecast = {
  tenant_id: string;
  storage_gb: number;
  storage_growth_pct: number;
  api_calls_hour: number;
  api_growth_pct: number;
  ai_usage_hour: number;
  ai_growth_pct: number;
  db_size_gb: number;
  cost_growth_pct: number;
  by_service?: Record<string, number>;
  generated_at: string;
};

export type ActivityEntry = {
  id: string;
  category: string;
  action: string;
  message: string;
  created_at: string;
};

const API_BASE = import.meta.env.VITE_SRE_API ?? '/api/v1/sre';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const sreApi = {
  dashboard: () => fetchJSON<SREDashboard>('/dashboard'),
  listHealth: () => fetchJSON<ComponentHealth[]>('/health/components'),
  listDeployments: () => fetchJSON<DeploymentRecord[]>('/deployments'),
  createDeployment: (body: { service: string; version: string; strategy?: string; region?: string }) =>
    fetchJSON<DeploymentRecord>('/deployments', { method: 'POST', body: JSON.stringify(body) }),
  rollbackDeployment: (id: string) =>
    fetchJSON<DeploymentRecord>(`/deployments/${id}/rollback`, { method: 'POST' }),
  listBackups: () => fetchJSON<BackupRecord[]>('/backups'),
  triggerBackup: (body: { type: string; region?: string }) =>
    fetchJSON<BackupRecord>('/backups', { method: 'POST', body: JSON.stringify(body) }),
  verifyBackup: (id: string) =>
    fetchJSON<BackupRecord>(`/backups/${id}/verify`, { method: 'POST' }),
  listRecoveries: () => fetchJSON<RecoveryRecord[]>('/recoveries'),
  startRecovery: (body: { type: string; source_region?: string; target_region?: string }) =>
    fetchJSON<RecoveryRecord>('/recoveries', { method: 'POST', body: JSON.stringify(body) }),
  validateRecovery: (id: string) =>
    fetchJSON<RecoveryRecord>(`/recoveries/${id}/validate`, { method: 'POST' }),
  listIncidents: () => fetchJSON<IncidentRecord[]>('/incidents'),
  createIncident: (body: { title: string; severity?: string; service?: string }) =>
    fetchJSON<IncidentRecord>('/incidents', { method: 'POST', body: JSON.stringify(body) }),
  resolveIncident: (id: string) =>
    fetchJSON<IncidentRecord>(`/incidents/${id}/resolve`, { method: 'POST' }),
  listRunbooks: () => fetchJSON<Runbook[]>('/runbooks'),
  listSLOs: () => fetchJSON<SLODefinition[]>('/slo'),
  listSLIs: () => fetchJSON<SLIDefinition[]>('/sli'),
  capacityForecast: () => fetchJSON<CapacityForecast>('/capacity'),
  listActivity: () => fetchJSON<ActivityEntry[]>('/activity'),
};
